import React, { useMemo } from "react";
import type { WordTime } from "../../hooks/useDialProof";
import type { Anchor, FlatMatch } from "../../utils/scriptMatch";

// Green "this is on-script" highlight, shared by word + flat mode. Soft emerald
// fill plus a solid underline, distinct from the blue playhead highlight. When a
// jump handler is wired, matched spans get a pointer + hover to signal "click me".
const MATCH_CLASS =
  "bg-emerald-400/20 text-glass rounded px-0.5 shadow-[inset_0_-2px_0_rgba(16,185,129,0.55)]";

// =============================================================================
// TranscriptView — the ONE transcript renderer, shared by every voice surface.
// =============================================================================
// Extracted from DialProofModal's CallDetailPanel so the dial proof drawer, the
// connects panel, the dial-due feed, and anything else that plays a recording
// render an identical transcript: word-timestamps → line-grouped view with a
// clickable m:ss stamp per line + every word individually click-to-seek and lit
// under the playhead; else the flat whisper text; else an empty note.
//
// Pure + stateless: the PARENT owns the RecordingPlayer, feeds `currentMs` (the
// playhead, from the player's onTimeUpdate) for the highlight, and wires
// `onSeekMs` to playerRef.seekTo(ms/1000). No player coupling lives here.
// =============================================================================

const LINE_WORD_CAP = 12;
type IndexedWord = { w: WordTime; idx: number };

// Group the flat word-times into short, readable LINES without re-transcribing.
// Whisper's own segment breaks track sentence punctuation, dialogue dashes, and a
// short line length — all of which survive in the stored word array. So we rebuild
// that "natural segment" look at render time: new line at a sentence end (. ? !),
// at a whisper dialogue-dash ("-"), or once a line hits the word cap. Every word
// keeps its GLOBAL index, so click-to-seek and playhead highlight are unchanged.
function groupWordsIntoLines(words: WordTime[]): IndexedWord[][] {
  const lines: IndexedWord[][] = [];
  let cur: IndexedWord[] = [];
  const isDash = (s: string) => s === "-" || s === "–" || s === "—";
  words.forEach((w, idx) => {
    const st = w.word.trim();
    if (isDash(st)) {
      if (cur.length) lines.push(cur);
      cur = [{ w, idx }];
      return;
    }
    cur.push({ w, idx });
    const last = st.slice(-1);
    if (last === "." || last === "?" || last === "!" || cur.length >= LINE_WORD_CAP) {
      lines.push(cur);
      cur = [];
    }
  });
  if (cur.length) lines.push(cur);
  return lines;
}

// ms → m:ss for the per-line timestamp gutter.
const formatMs = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

interface TranscriptViewProps {
  words: WordTime[] | null;
  flatTranscript: string | null;
  /** Playhead position in ms (from the player's onTimeUpdate) — drives the word highlight. */
  currentMs?: number | null;
  /** Seek the player to this position, in ms. */
  onSeekMs?: (ms: number) => void;
  /** Word index → the course anchor it matched — highlighted green (word mode). */
  highlightWordIdx?: Map<number, Anchor> | null;
  /** Char ranges that matched the course script — highlighted green (flat mode). */
  highlightRanges?: FlatMatch[] | null;
  /** Click a green (on-script) span → teleport the course panel to that phrase. */
  onScriptJump?: (anchor: Anchor) => void;
  emptyLabel?: string;
  className?: string;
}

const TranscriptView: React.FC<TranscriptViewProps> = ({
  words,
  flatTranscript,
  currentMs = null,
  onSeekMs,
  highlightWordIdx = null,
  highlightRanges = null,
  onScriptJump,
  emptyLabel = "No transcript for this call.",
  className = "",
}) => {
  const lines = useMemo(() => (words ? groupWordsIntoLines(words) : null), [words]);
  // The word under the playhead. Linear scan is cheap (hundreds of words, ~4 ticks/sec).
  const activeWordIdx = useMemo(() => {
    if (!words || currentMs == null) return null;
    const i = words.findIndex((w) => currentMs >= w.start_ms && currentMs < w.end_ms);
    return i === -1 ? null : i;
  }, [words, currentMs]);
  const seek = (ms: number) => onSeekMs?.(ms);

  if (lines) {
    return (
      <div data-testid="transcript-words" className={`token-13 text-glass-secondary ${className}`}>
        {lines.map((line, li) => {
          const startMs = line[0]?.w.start_ms ?? 0;
          return (
            <div key={li} className="flex gap-3 mb-2 last:mb-0" data-testid="transcript-line">
              <button
                type="button"
                onClick={() => seek(startMs)}
                className="anim-tap token-11 font-mono text-glass-muted hover:text-ds-accent shrink-0 w-11 text-right pt-0.5"
                aria-label={`Jump to ${formatMs(startMs)}`}
              >
                {formatMs(startMs)}
              </button>
              <div className="flex-1 leading-relaxed">
                {line.map(({ w, idx }) => {
                  const anchor = highlightWordIdx?.get(idx) ?? null;
                  // A green word jumps to the course when a jump handler exists;
                  // otherwise (and every plain word) it seeks the audio.
                  const jumps = anchor != null && !!onScriptJump;
                  const act = () => (jumps ? onScriptJump!(anchor!) : seek(w.start_ms));
                  return (
                    <span
                      key={idx}
                      role="button"
                      tabIndex={0}
                      data-testid="transcript-word"
                      data-script-match={anchor ? "1" : undefined}
                      title={jumps ? "Jump to this line in the course →" : undefined}
                      onClick={act}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          act();
                        }
                      }}
                      className={`cursor-pointer rounded px-0.5 transition-colors ${
                        idx === activeWordIdx
                          ? "bg-ds-accent/20 text-glass"
                          : anchor
                            ? `${MATCH_CLASS} ${jumps ? "hover:bg-emerald-400/35" : ""}`
                            : "hover:bg-black/[0.06]"
                      }`}
                    >
                      {w.word}{" "}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (flatTranscript) {
    const ranges = highlightRanges ?? [];
    let body: React.ReactNode = flatTranscript;
    if (ranges.length) {
      const parts: React.ReactNode[] = [];
      let cursor = 0;
      ranges.forEach((r, k) => {
        if (r.start > cursor) parts.push(flatTranscript.slice(cursor, r.start));
        const jumps = !!onScriptJump;
        parts.push(
          <mark
            key={k}
            data-script-match="1"
            title={jumps ? "Jump to this line in the course →" : undefined}
            onClick={jumps ? () => onScriptJump!(r.anchor) : undefined}
            className={`${MATCH_CLASS} ${jumps ? "cursor-pointer hover:bg-emerald-400/35" : ""}`}
          >
            {flatTranscript.slice(r.start, r.end)}
          </mark>
        );
        cursor = r.end;
      });
      if (cursor < flatTranscript.length) parts.push(flatTranscript.slice(cursor));
      body = parts;
    }
    return (
      <p
        data-testid="transcript-words"
        className={`token-13 text-glass-secondary whitespace-pre-wrap leading-relaxed ${className}`}
      >
        {body}
      </p>
    );
  }

  return <p className={`token-13 text-glass-muted ${className}`}>{emptyLabel}</p>;
};

export default TranscriptView;
